import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import type { User } from '@supabase/supabase-js'
import type { AuthResponse } from '@supabase/supabase-js'

interface SignUpData {
  email: string
  password: string
  first_name: string
  last_name: string
  state: string
}

interface AuthContextType {
  user: User | null
  signUp: (data: SignUpData) => Promise<AuthResponse>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    // Verifica se já existe uma sessão
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user)
      }
    })

    // Inscreve para mudanças na autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (data: SignUpData) => {
    const { email, password, first_name, last_name, state } = data
    
    const authResponse = await supabase.auth.signUp({
      email,
      password,
    })

    if (authResponse.error) {
      throw authResponse.error
    }

    if (authResponse.data.user) {
      // Cria o perfil do médico
      const { error: profileError } = await supabase
        .from('doctor_profiles')
        .insert({
          user_id: authResponse.data.user.id,
          first_name,
          last_name,
          state,
          email,
        })

      if (profileError) {
        // Se houver erro ao criar o perfil, deleta o usuário criado
        await supabase.auth.admin.deleteUser(authResponse.data.user.id)
        throw profileError
      }
    }

    return authResponse
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw error
    }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ user, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 